<template>
    <section class="page product-order-details-page page--gradient-background row">
        <section class="row main">
            <div class="wrapper">
                <header class="row__header">
                    <back-button class="back-button"
                        :isBlack="true"
                        :isAbsolute="false"
                    />
                    <h1 id="product-order-details-text-title">
                        {{ $t('rewards.fanShop.orderSummary.title') }}
                    </h1>
                </header>
                <widget-form id="product-order-details-form"
                    :title="formTitle"
                    class="widget widget--transparent widget--fluid"
                >
                    <form-field v-for="(field, index) in fieldData"
                        :id="`product-order-details-field-${index}`"
                        :key="index"
                        :editable="field.isEditable"
                        :name="field.name"
                        :field="field"
                        :type="field.fieldType"
                        :options="field.options"
                        :placeholder="field.placeholder"
                        :displayOrder="field.displayOrder"
                        @input="(obj) => fieldData[index].value = obj.value"
                    />
                    <div class="widget__buttons">
                        <button v-if="step > 0"
                            id="product-order-details-button-prev-ticket"
                            class="widget__button"
                            @click="goToPrevious"
                        >
                            {{ $t('rewards.fanShop.orderDeliveryDetails.previousTicket') }}
                        </button>
                        <button id="product-order-details-button-next-ticket"
                            :class="['widget__button', {'widget__button--disabled': missingFields},
                                     {'widget__button--margin-left': step > 0}]"
                            @click="proceed"
                        >
                            {{ proceedBtnText }}
                        </button>
                    </div>
                </widget-form>
            </div>
        </section>
    </section>
</template>

<script>
// 3rd party lib
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Local Types
import localStoreTypes from '@webapp/store/types.js';

// Utils
import {
    hasNotEnoughProductStockError,
} from '@shared/utils/error.js';

// Components
import widgetForm from '@webapp/components/form/widget-form.vue';
import backButton from '@webapp/components/back-button/back-button.vue';
import formField from '@webapp/modules/rewards/components/fanshop/form-field.vue';

export default {
    name: 'product-order-details',
    components: {
        backButton,
        widgetForm,
        formField,
    },
    props: {
        variant: {
            type: String,
            default: null,
        },
        // Coming from human readable url, that's why it's 1
        stepNumber: {
            type: String,
            default: '1',
        },
    },
    data() {
        return {
            fieldData: [],
            // Now we change step to be an index
            step: Number(this.stepNumber) - 1,
            missingFields: true,
        };
    },
    computed: {
        ...mapGetters(['getLatestSimpleValidationError']),

        ...mapGetters('fanshop', [
            'getCheckoutStartProcessResponse',
            'getDeliveryDetailsByIndex',
            'getDeliveryDetailsCount',
            'getDeliveryDetails',
        ]),

        isLastStep() {
            return this.step + 1 >= this.getDeliveryDetailsCount;
        },

        formTitle() {
            return this.getDeliveryDetailsCount > 1
                ? this.$t('rewards.fanShop.orderDeliveryDetails.ticketDetailsMultiple', {
                    from: this.step + 1,
                    until: this.getDeliveryDetailsCount,
                })
                : this.$t('rewards.fanShop.orderDeliveryDetails.ticketDetails');
        },

        proceedBtnText() {
            return this.isLastStep
                ? this.$t('rewards.fanShop.orderDeliveryDetails.proceed')
                : this.$t('rewards.fanShop.orderDeliveryDetails.nextTicket');
        },
    },
    watch: {
        getLatestSimpleValidationError: 'onError',
        fieldData: {
            handler() {
                this.checkMissingFields();
            },
            deep: true,
        },
    },
    created() {
        this.loadStepData(this.step);
    },
    methods: {
        ...mapActions({
            showAppLoader: localStoreTypes.SET_APP_LOADER,
        }),

        ...mapActions('fanshop', ['updateCheckoutData']),

        checkMissingFields() {
            if (this.fieldData?.length) {
                this.missingFields = Boolean(this.fieldData.find((field) => !field.isOptional && !field.value));
            } else {
                this.missingFields = false;
            }
        },

        async proceed() {
            if (!this.missingFields) {
                await this.updateCheckoutData({
                    step: this.step,
                    data: this.fieldData,
                });

                if (this.step + 1 >= this.getDeliveryDetailsCount) {
                    this.$router.push(`/rewards/fan-rewards/place-order/${this.variant}`);
                } else {
                    this.$router.push(`/rewards/fan-rewards/order-summary/${this.variant}/${this.step + 2}`);
                }
            }
        },

        async goToPrevious() {
            if (this.step > 0) {
                await this.updateCheckoutData({
                    step: this.step,
                    data: this.fieldData,
                });

                this.$router.push(`/rewards/fan-rewards/order-summary/${this.variant}/${this.step}`);
            }
        },

        loadStepData(step) {
            const fieldData = [...this.getDeliveryDetailsByIndex(step)];

            this.fieldData = fieldData.map((obj) => ({
                ...obj,
            }));
        },

        /**
         * On api error
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onError(error) {
            let errorMessage = '';

            if (hasNotEnoughProductStockError(error)) {
                errorMessage = this.$t('rewards.fanShop.orderSummary.notEnoughProductStockError');
            } else {
                const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                errorMessage = fetchErrors.message;
            }

            if (!this.leftPage) {
                this.$notify({
                    type: 'error',
                    text: errorMessage,
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.product-order-details-page {
    color: $daintree-color;
    padding-bottom: 7rem;

    .row {
        &__header {
            .back-button {
                margin-right: 1.5rem;
                left: 0;
            }

            h1 {
                padding: 0;
            }
        }
    }

    .page-title {
        font-size: 2rem;
    }

    ::v-deep .multiselect--disabled {
        cursor: not-allowed;
        pointer-events: auto;
    }
}

.wrapper {
    width: 95%;

    @include min-width(md) {
        width: 85%;
    }

    @include min-width(xl) {
        width: 92%;
    }
}

.widget {
    border: none;

    &__buttons {
        display: flex;
        width: 100%;
        justify-content: space-between;

        .widget__button {
            @include min-width(md) {
                width: 21.9rem;
            }

            &:last-child {
                margin-left: auto;
            }

            &--disabled {
                background-color: $lima-color-opaque;
                color: $hit-gray-color;
            }

            &--margin-left {
                margin-left: 1.6rem !important;
            }
        }
    }
}
</style>